'use client';

import { useCallback, useEffect, useLayoutEffect, useRef, type RefObject } from 'react';
import useListNavigationStore, {
  currentHistoryToken,
  isOverlayHistoryEntry,
  tagCurrentHistoryEntry,
  type ListPosition,
} from '@/store/useListNavigationStore';

/**
 * Cuántos intentos se hacen, ya con el listado cargado, antes de rendirse.
 * ~4s a 60fps. La ventana es larga a propósito: en los homes del Builder hay
 * secciones ARRIBA de la grilla (hero, carrusel, imágenes sin alto reservado)
 * que terminan de cargar bastante después que la lista. Medido en Demo
 * Argentina: la grilla estaba lista a los 181ms y el contenido de arriba
 * seguía creciendo a los 933ms.
 */
const MAX_TRIES = 240;

/** Cuántos frames seguidos tiene que estar quieta la posición para darla por buena. */
const FRAMES_ESTABLE = 90;

/** Espera tras el último scroll antes de anotar la posición. */
const GUARDADO_MS = 200;

interface Options {
  /**
   * Distingue listados cuando conviven en la misma ruta o se repiten entre
   * rutas: 'vehicles' para /vehicles, 'home' para la grilla del home.
   */
  scope: string;
  /**
   * Filtros, orden y búsqueda serializados. Si al volver no coinciden con los
   * de la foto, la posición guardada no describe esta lista y se descarta.
   */
  signature: string;
  /** false mientras cargan vehículos o catálogos: la lista aún no tiene alto. */
  ready: boolean;
  /** Contenedor de la grilla: de ahí sale la tarjeta que sirve de ancla. */
  gridRef: RefObject<HTMLElement | null>;
}

/**
 * Conserva la posición de un listado paginado entre el listado y la ficha.
 *
 * Devuelve con cuántas tarjetas hay que arrancar (`initialCursor`, pensado para
 * el inicializador de `useState`) y un `remember` con el que el listado informa
 * su paginación.
 *
 * La foto se indexa por ENTRADA DEL HISTORIAL —ver `useListNavigationStore`—,
 * así que volver con Atrás la encuentra y entrar por un link nuevo no. Y se
 * mantiene mientras esa entrada exista: Atrás → Adelante → Atrás tiene que
 * devolver al mismo lugar las veces que haga falta.
 *
 * La posición se guarda ANCLADA A UNA TARJETA (qué vehículo estaba arriba y a
 * qué altura de la pantalla), no como un número de scroll. Un número solo no
 * alcanza: si algo que está por encima de la grilla carga tarde, la lista entera
 * baja y el mismo scrollY ya apunta a otros autos.
 */
export function useListNavigationMemory({ scope, signature, ready, gridRef }: Options) {
  // Lectura por render y sin efectos secundarios: si el componente se remonta
  // (StrictMode, o un render descartado por Suspense) vuelve a dar lo mismo.
  const pending = useRef<ListPosition | null | undefined>(undefined);
  const token = useRef<string | null>(null);

  if (pending.current === undefined) {
    token.current = currentHistoryToken(scope);
    const stored = token.current
      ? useListNavigationStore.getState().positions[token.current]
      : undefined;
    pending.current = stored ?? null;
  }

  const initialCursor = pending.current?.cursor ?? null;

  /** Último valor informado por el listado. Lo actualiza `remember`. */
  const cursorRef = useRef(initialCursor ?? 1);

  // Por ref para que los callbacks no cambien de identidad en cada render: el
  // efecto que los usa corre después de CADA commit y no debe reinstalarse.
  const readyRef = useRef(ready);
  readyRef.current = ready;
  const signatureRef = useRef(signature);
  signatureRef.current = signature;
  const tries = useRef(0);
  const estable = useRef(0);
  /** ¿Ya se dejó al usuario en su posición al menos una vez? */
  const aplicado = useRef(false);

  /** Tarjetas de vehículo montadas en la grilla, en orden. */
  const tarjetas = useCallback((): HTMLAnchorElement[] => {
    const raiz = gridRef.current;
    if (!raiz) return [];
    return [...raiz.querySelectorAll<HTMLAnchorElement>('a[href^="/vehicles/"]')];
  }, [gridRef]);

  /** Anota dónde está parado el usuario, bajo la entrada actual del historial. */
  const guardar = useCallback(() => {
    if (!readyRef.current) return;
    // Antes de dejar al usuario en su posición no se escribe: el scroll 0 del
    // arranque pisaría la foto justo antes de poder usarla. Pero una vez que
    // ya está puesto, sí hay que seguir anotando aunque la ventana de
    // restauración siga abierta —dura hasta 4s—: si no, el comprador que
    // vuelve y toca otro auto enseguida se lleva la foto anterior y el
    // siguiente Atrás lo deja en el lugar equivocado. Medido en WebKit.
    if (pending.current !== null && !aplicado.current) return;
    // Con una capa abierta (visor, menú, drawer) la entrada de arriba es la de
    // la capa, no la del listado: marcarla dejaría la foto colgada de una
    // entrada que desaparece al cerrarla.
    if (isOverlayHistoryEntry()) return;
    // El token se resuelve SIEMPRE contra la entrada actual; cachearlo en un
    // ref lo dejaba apuntando a la entrada equivocada después de abrir y
    // cerrar una capa.
    const t = currentHistoryToken(scope) || tagCurrentHistoryEntry(scope);
    if (!t) return;
    token.current = t;

    // Ancla: la primera tarjeta que todavía se ve, y a qué altura de la
    // pantalla está. Es lo que hay que recuperar, más que el número de scroll.
    let anchorHref: string | undefined;
    let anchorTop: number | undefined;
    for (const a of tarjetas()) {
      const r = a.getBoundingClientRect();
      if (r.height > 0 && r.bottom > 0) {
        anchorHref = a.getAttribute('href') || undefined;
        anchorTop = Math.round(r.top);
        break;
      }
    }

    useListNavigationStore.getState().remember(t, {
      cursor: cursorRef.current,
      scrollY: Math.round(window.scrollY),
      signature: signatureRef.current,
      anchorHref,
      anchorTop,
    });
  }, [scope, tarjetas]);

  /** A qué scrollY hay que ir, según la foto. null = todavía no se puede saber. */
  const destinoDe = useCallback(
    (target: ListPosition): number | null => {
      if (target.anchorHref && typeof target.anchorTop === 'number') {
        const a = tarjetas().find((x) => x.getAttribute('href') === target.anchorHref);
        if (a) {
          const abs = a.getBoundingClientRect().top + window.scrollY;
          const max = document.documentElement.scrollHeight - window.innerHeight;
          return Math.max(0, Math.min(max, Math.round(abs - target.anchorTop)));
        }
        // La tarjeta todavía no está montada: esperar, no caer al número.
        return null;
      }
      // Fotos viejas (sin ancla) y listados sin tarjeta visible al salir.
      const max = document.documentElement.scrollHeight - window.innerHeight;
      return max >= target.scrollY - 2 ? target.scrollY : null;
    },
    [tarjetas]
  );

  const resolver = useCallback(() => {
    pending.current = null;
  }, []);

  const tryRestore = useCallback(() => {
    const target = pending.current;
    if (!target) return;

    // Mientras carga, el listado no tiene alto todavía y no se gastan intentos.
    if (!readyRef.current) return;

    // Los filtros cambiaron entre la salida y la vuelta: la foto es de otra lista.
    if (target.signature !== signatureRef.current) {
      resolver();
      return;
    }

    const destino = destinoDe(target);
    if (destino === null) {
      if (++tries.current > MAX_TRIES) resolver();
      return;
    }

    const desvio = window.scrollY - destino;

    if (Math.abs(desvio) <= 2) {
      // Ya está donde corresponde. Cuenta como aplicado aunque no haya hecho
      // falta scrollear: si no, el guardado quedaba bloqueado toda la ventana
      // y volver y tocar otro auto enseguida se llevaba la foto anterior.
      aplicado.current = true;
      estable.current++;
    } else if (!aplicado.current || Math.abs(desvio) < window.innerHeight / 2) {
      // O todavía no lo ubicamos, o la lista se corrió un poco porque algo de
      // arriba terminó de cargar: se vuelve a anclar.
      window.scrollTo(0, destino);
      aplicado.current = true;
      estable.current = 0;
    } else {
      // Ya estaba ubicado y ahora el scroll está MUY lejos: eso no es un
      // reacomodo del layout, es que se movió de verdad (él, o la página
      // llevándolo a otra tarjeta). Manda eso, no la foto vieja.
      resolver();
      return;
    }

    // No se suelta en el primer acierto: lo que está por encima de la grilla
    // puede seguir creciendo y correr la lista. Se suelta cuando la posición
    // se mantuvo quieta un rato, o al agotar la ventana.
    if (estable.current >= FRAMES_ESTABLE || ++tries.current > MAX_TRIES) resolver();
  }, [destinoDe, resolver]);

  // Marca la entrada del historial apenas se monta el listado, para que volver
  // con Atrás a ESTA entrada encuentre su foto las veces que haga falta.
  useLayoutEffect(() => {
    if (isOverlayHistoryEntry()) return;
    if (!token.current) token.current = tagCurrentHistoryEntry(scope);
  }, [scope]);

  // Después de CADA commit, y antes de pintar. El commit que monta la grilla
  // completa es justo el que tiene que dejar el scroll puesto.
  useLayoutEffect(() => {
    tryRestore();
  });

  // El commit que hace crecer la página no siempre es del componente que usa
  // este hook: en /vehicles el alto aparece cuando el `ClientOnly` interno se
  // monta, y un estado del hijo no vuelve a correr los efectos del padre. El
  // ResizeObserver sí avisa, y su callback corre después del layout y ANTES de
  // pintar, que es la única ventana en la que poner el scroll no se ve.
  useLayoutEffect(() => {
    if (!pending.current || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => tryRestore());
    observer.observe(document.documentElement);
    return () => observer.disconnect();
  }, [tryRestore]);

  // Red de seguridad mientras dure la ventana de restauración.
  useLayoutEffect(() => {
    if (!pending.current) return;
    let cancelled = false;
    let id = 0;
    const loop = () => {
      if (cancelled || !pending.current) return;
      tryRestore();
      id = requestAnimationFrame(loop);
    };
    id = requestAnimationFrame(loop);
    return () => {
      cancelled = true;
      cancelAnimationFrame(id);
    };
  }, [tryRestore]);

  // Si el usuario toca la pantalla, la rueda o el teclado, manda él: se corta
  // la restauración en el acto para no pelearle el scroll.
  useEffect(() => {
    if (!pending.current) return;
    const rendirse = () => resolver();
    const opts = { passive: true } as const;
    window.addEventListener('touchstart', rendirse, opts);
    window.addEventListener('wheel', rendirse, opts);
    window.addEventListener('keydown', rendirse);
    return () => {
      window.removeEventListener('touchstart', rendirse);
      window.removeEventListener('wheel', rendirse);
      window.removeEventListener('keydown', rendirse);
    };
  }, [resolver]);

  // Seguimiento continuo: la foto tiene que reflejar dónde quedó el usuario,
  // no sólo dónde estaba la última vez que tocó una tarjeta. Se escribe con
  // `getState()`, sin suscripción, así que no provoca re-renders.
  useEffect(() => {
    let id: ReturnType<typeof setTimeout>;
    const onScroll = () => {
      clearTimeout(id);
      id = setTimeout(guardar, GUARDADO_MS);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      clearTimeout(id);
      window.removeEventListener('scroll', onScroll);
    };
  }, [guardar]);

  /**
   * Informa cuánto hay montado y anota la posición.
   *
   * Lo llama el listado al montar, cada vez que cambia su paginación y —sobre
   * todo— al tocar una tarjeta: el seguimiento por scroll va con retardo y el
   * click puede llegar antes de que haya escrito.
   */
  const remember = useCallback(
    (valor: number) => {
      if (typeof window === 'undefined') return;
      cursorRef.current = valor;
      guardar();
    },
    [guardar]
  );

  return { initialCursor, remember };
}

export default useListNavigationMemory;
